import { request } from '@umijs/max';
import { ADMIN_COMPANY_PREFIX, ADMIN_PLATFORM_PREFIX } from '@/utils/adminApi';

export type PagedResult<T> = {
  data: T[];
  total: number;
  success: boolean;
};

type ApiResponse<T> = {
  status: number;
  error_code?: number;
  error_msg?: string;
  result_body?: T;
};

export type CompanyItem = {
  id: string;
  key: string;
  name: string;
  enabled: boolean;
  adminLoginMiniProgramId?: string;
  adminLoginMiniProgramName?: string;
  adminLoginMiniProgramAppId?: string;
  isPlatformOwner?: boolean;
  createdAt: string;
  updatedAt: string;
};

export type MiniProgramItem = {
  id: string;
  companyId: string;
  companyKey?: string;
  companyName?: string;
  companyIsPlatformOwner?: boolean;
  type: 'app' | 'platform_admin' | string;
  key: string;
  name: string;
  appId: string;
  hasAppSecret: boolean;
  fallAlertSubscribeTemplateId?: string;
  deviceLimit: number;
  maintenanceMessage?: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

type BackendCompany = {
  id: string | number;
  key: string;
  name: string;
  enabled: boolean;
  admin_login_mini_program_id?: string | number;
  admin_login_mini_program_name?: string;
  admin_login_mini_program_app_id?: string;
  is_platform_owner?: boolean;
  created_at?: string;
  updated_at?: string;
};

type BackendMiniProgram = {
  id: string | number;
  company_id: string | number;
  company_key?: string;
  company_name?: string;
  company_is_platform_owner?: boolean;
  type: string;
  key?: string;
  name: string;
  app_id: string;
  has_app_secret?: boolean;
  fall_alert_subscribe_template_id?: string;
  device_limit?: number;
  maintenance_message?: string;
  enabled: boolean;
  created_at?: string;
  updated_at?: string;
};

const unwrap = <T>(response: ApiResponse<T>): T => {
  if (response.status !== 1) {
    throw new Error(response.error_msg || '请求失败');
  }
  return response.result_body as T;
};

const normalizeOptionalBoolean = (
  value: boolean | string | undefined,
): boolean | undefined => {
  if (value === undefined || value === '') {
    return undefined;
  }
  return value === true || value === 'true';
};

const normalizeCompany = (item: BackendCompany): CompanyItem => ({
  id: String(item.id || ''),
  key: item.key || '',
  name: item.name || '',
  enabled: Boolean(item.enabled),
  adminLoginMiniProgramId:
    item.admin_login_mini_program_id === undefined ||
    item.admin_login_mini_program_id === null
      ? undefined
      : String(item.admin_login_mini_program_id),
  adminLoginMiniProgramName: item.admin_login_mini_program_name,
  adminLoginMiniProgramAppId: item.admin_login_mini_program_app_id,
  isPlatformOwner: Boolean(item.is_platform_owner),
  createdAt: item.created_at || '',
  updatedAt: item.updated_at || '',
});

const normalizeMiniProgram = (item: BackendMiniProgram): MiniProgramItem => ({
  id: String(item.id || ''),
  companyId: String(item.company_id || ''),
  companyKey: item.company_key,
  companyName: item.company_name,
  companyIsPlatformOwner: Boolean(item.company_is_platform_owner),
  type: item.type || 'app',
  key: item.key || '',
  name: item.name || '',
  appId: item.app_id || '',
  hasAppSecret: Boolean(item.has_app_secret),
  fallAlertSubscribeTemplateId: item.fall_alert_subscribe_template_id,
  deviceLimit: item.device_limit ?? -1,
  maintenanceMessage: item.maintenance_message || '',
  enabled: Boolean(item.enabled),
  createdAt: item.created_at || '',
  updatedAt: item.updated_at || '',
});

export async function getCompanies(params: {
  current?: number;
  pageSize?: number;
  key?: string;
  name?: string;
  enabled?: boolean | string;
}): Promise<PagedResult<CompanyItem>> {
  const result = unwrap(
    await request<ApiResponse<{ list: BackendCompany[]; total: number }>>(
      `${ADMIN_PLATFORM_PREFIX}/companies`,
      {
        method: 'GET',
        params: {
          page: params.current,
          limit: params.pageSize,
          key: params.key,
          name: params.name,
          enabled: normalizeOptionalBoolean(params.enabled),
        },
      },
    ),
  );
  return {
    data: (result.list || []).map(normalizeCompany),
    total: result.total || 0,
    success: true,
  };
}

export async function createCompany(params: {
  key: string;
  name: string;
  enabled?: boolean;
}) {
  const result = unwrap(
    await request<ApiResponse<BackendCompany>>(
      `${ADMIN_PLATFORM_PREFIX}/companies`,
      {
        method: 'POST',
        data: params,
      },
    ),
  );
  return normalizeCompany(result);
}

export async function updateCompany(params: {
  id: string;
  name: string;
  enabled: boolean;
  adminLoginMiniProgramId?: string;
}) {
  const result = unwrap(
    await request<ApiResponse<BackendCompany>>(
      `${ADMIN_PLATFORM_PREFIX}/companies`,
      {
        method: 'PUT',
        data: {
          id: params.id,
          name: params.name,
          enabled: params.enabled,
          admin_login_mini_program_id: params.adminLoginMiniProgramId || '0',
        },
      },
    ),
  );
  return normalizeCompany(result);
}

export async function getLoginSettings() {
  const result = unwrap(
    await request<
      ApiResponse<{
        companies: BackendCompany[];
        platform_owner_company_id?: string | number;
      }>
    >(`${ADMIN_PLATFORM_PREFIX}/login-settings`, {
      method: 'GET',
    }),
  );
  return {
    companies: (result.companies || []).map(normalizeCompany),
    platformOwnerCompanyId:
      result.platform_owner_company_id === undefined ||
      result.platform_owner_company_id === null
        ? undefined
        : String(result.platform_owner_company_id),
  };
}

export async function updateLoginSettings(params: {
  platformOwnerCompanyId: string;
}) {
  const result = unwrap(
    await request<
      ApiResponse<{
        companies: BackendCompany[];
        platform_owner_company_id?: string | number;
      }>
    >(`${ADMIN_PLATFORM_PREFIX}/login-settings`, {
      method: 'PUT',
      data: {
        platform_owner_company_id: params.platformOwnerCompanyId,
      },
    }),
  );
  return {
    companies: (result.companies || []).map(normalizeCompany),
    platformOwnerCompanyId:
      result.platform_owner_company_id === undefined ||
      result.platform_owner_company_id === null
        ? undefined
        : String(result.platform_owner_company_id),
  };
}

export async function deleteCompany(id: string) {
  unwrap(
    await request<ApiResponse<any>>(`${ADMIN_PLATFORM_PREFIX}/companies`, {
      method: 'DELETE',
      params: { id },
    }),
  );
}

export async function getMiniPrograms(params: {
  current?: number;
  pageSize?: number;
  companyId?: string;
  appId?: string;
  type?: string;
  enabled?: boolean | string;
}): Promise<PagedResult<MiniProgramItem>> {
  const result = unwrap(
    await request<ApiResponse<{ list: BackendMiniProgram[]; total: number }>>(
      `${ADMIN_PLATFORM_PREFIX}/mini-programs`,
      {
        method: 'GET',
        params: {
          page: params.current,
          limit: params.pageSize,
          company_id: params.companyId,
          app_id: params.appId,
          type: params.type,
          enabled: normalizeOptionalBoolean(params.enabled),
        },
      },
    ),
  );
  return {
    data: (result.list || []).map(normalizeMiniProgram),
    total: result.total || 0,
    success: true,
  };
}

export async function getCompanyMiniPrograms() {
  const result = unwrap(
    await request<ApiResponse<{ list: BackendMiniProgram[]; total: number }>>(
      `${ADMIN_COMPANY_PREFIX}/mini-programs`,
      {
        method: 'GET',
        params: {
          page: 1,
          limit: 100,
        },
      },
    ),
  );

  return (result.list || []).map(normalizeMiniProgram);
}

export async function createMiniProgram(params: {
  companyId: string;
  type?: string;
  key: string;
  name: string;
  appId: string;
  appSecret?: string;
  fallAlertSubscribeTemplateId?: string;
  deviceLimit?: number;
  maintenanceMessage?: string;
  enabled?: boolean;
}) {
  const result = unwrap(
    await request<ApiResponse<BackendMiniProgram>>(
      `${ADMIN_PLATFORM_PREFIX}/mini-programs`,
      {
        method: 'POST',
        data: {
          company_id: params.companyId,
          type: params.type,
          key: params.key,
          name: params.name,
          app_id: params.appId,
          app_secret: params.appSecret,
          fall_alert_subscribe_template_id: params.fallAlertSubscribeTemplateId,
          device_limit: params.deviceLimit ?? -1,
          maintenance_message: params.maintenanceMessage,
          enabled: params.enabled,
        },
      },
    ),
  );
  return normalizeMiniProgram(result);
}

export async function updateMiniProgram(params: {
  id: string;
  key: string;
  name: string;
  appId: string;
  appSecret?: string;
  fallAlertSubscribeTemplateId?: string;
  deviceLimit: number;
  maintenanceMessage?: string;
  enabled: boolean;
}) {
  const result = unwrap(
    await request<ApiResponse<BackendMiniProgram>>(
      `${ADMIN_PLATFORM_PREFIX}/mini-programs`,
      {
        method: 'PUT',
        data: {
          id: params.id,
          key: params.key,
          name: params.name,
          app_id: params.appId,
          app_secret: params.appSecret,
          fall_alert_subscribe_template_id: params.fallAlertSubscribeTemplateId,
          device_limit: params.deviceLimit,
          maintenance_message: params.maintenanceMessage,
          enabled: params.enabled,
        },
      },
    ),
  );
  return normalizeMiniProgram(result);
}

export async function deleteMiniProgram(id: string) {
  unwrap(
    await request<ApiResponse<any>>(`${ADMIN_PLATFORM_PREFIX}/mini-programs`, {
      method: 'DELETE',
      params: { id },
    }),
  );
}
